import {
  IIAMUser,
  IIAMUserPolicyRequest,
  IIAMUserRequest,
} from "api/entities/iam-users";
import DetailsPanel from "components/shared-ui/details-panel/details-panel";
import { DefaultRouteAction, DefaultRouteParam } from "constants/routes";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FormMode, formUtils } from "utils/form-utils";
import { useApi } from "utils/hooks/use-api";
import styles from "./user-panel.module.scss";
import { Formatters } from "helper/formatters";
import { iamService } from "api/services/iam";
import DetailsPanelFooter from "components/shared-ui/details-panel-footer/details-panel-footer";
import {
  IAMEntityNameSection,
  IIAMEntityNameSectionErrors,
} from "components/shared-ui/iam/sections/entity-name/entity-name-section";
import { IAMEntity } from "utils/validators";
import GroupSection from "./sections/group/group-section";
import IAMGroupPanel from "components/iam-groups/group-panel/group-panel";
import { IIAMGroup, IIAMGroupUserRequest } from "api/entities/iam-groups";
import AccessKeysSection from "./sections/access-keys/aceess-keys-section";
import PoliciesSection from "./sections/policies/policies-section";
import { IIAMAttachedPolicy } from "api/entities/iam-policies";
import IAMPolicyPanel from "components/iam-policies/policy-panel/policy-panel";
import { areArraysEqual } from "utils/array-utils";
import { Label } from "components/shared-ui/label";

export interface IIAMUserPanelProps {
  fetchUsers: () => void;
  mode?: FormMode;
  embedded: boolean;
  onClose: () => void;
}

export default function IAMUserPanel(props: IIAMUserPanelProps) {
  const { idOrAction } = useParams<{
    [DefaultRouteParam.IdOrAction]: string;
  }>();

  const [userApi, isUserLoading] = useApi();

  const open = props.embedded ? props.mode === FormMode.Create : !!idOrAction;
  const isUserCreation = props.embedded
    ? props.mode === FormMode.Create
    : idOrAction === DefaultRouteAction.New;

  const userName = isUserCreation ? null : idOrAction;

  const [user, setUser] = useState<IIAMUser>(null);
  const [currentGroups, setCurrentGroups] = useState<IIAMGroup[]>([]);
  const [SelectedGroups, setSelectGroups] = useState<IIAMGroup[]>([]);
  const [attachedPolicies, setAttachedPolicies] = useState<
    IIAMAttachedPolicy[]
  >([]);
  const [selectedPolicies, setSelectedPolicies] = useState<
    IIAMAttachedPolicy[]
  >([]);
  const [errors, setErrors] = useState(null);
  const [groupPanelFormMode, setGroupPanelFormMode] = useState<FormMode>(null);
  const [policyPanelFormMode, setPolicyPanelFormMode] =
    useState<FormMode>(null);
  const [userCreateRequest, setUserCreateRequest] = useState<IIAMUserRequest>({
    UserName: "",
  });

  const canProceed = () => {
    return (
      (!!errors &&
        !Object.keys(errors).some((key) => errors[key]) &&
        !!userCreateRequest.UserName &&
        userCreateRequest.UserName !== userName) ||
      !areArraysEqual(currentGroups, SelectedGroups) ||
      !areArraysEqual(attachedPolicies, selectedPolicies)
    );
  };

  const handleAddGroupFormPanelOpen = () => {
    setGroupPanelFormMode(FormMode.Create);
  };
  const handleAddGroupFormPanelClose = () => {
    setGroupPanelFormMode(null);
  };
  const handleAddPolicyFormPanelOpen = () => {
    setPolicyPanelFormMode(FormMode.Create);
  };
  const handleAddPolicyFormPanelClose = () => {
    setPolicyPanelFormMode(null);
  };
  const handleError = (errors: Partial<IIAMEntityNameSectionErrors>) => {
    setErrors((prevErrors) => ({ ...prevErrors, ...errors }));
  };

  const handleChange = (userUpdate: Partial<IIAMUserRequest>) => {
    setUserCreateRequest((prev) => ({ ...prev, ...userUpdate }));
  };

  const handleGroupChange = (groups: IIAMGroup[]) => {
    setSelectGroups(groups);
  };
  const handlePolicyChange = (groups: IIAMAttachedPolicy[]) => {
    setSelectedPolicies(groups);
  };

  const addToGroup = async (req: IIAMGroupUserRequest) => {
    await userApi(iamService.addUserToGroup(req));
  };
  const removeFromGroup = async (req: IIAMGroupUserRequest) => {
    await userApi(iamService.removeUserFromGroup(req));
  };
  const attachPolicy = async (req: IIAMUserPolicyRequest) => {
    await userApi(iamService.attachPolicy(req));
  };
  const detachPolicy = async (req: IIAMUserPolicyRequest) => {
    await userApi(iamService.detachPolicy(req));
  };

  const updateGroups = async (UserName: string) => {
    if (areArraysEqual(currentGroups, SelectedGroups)) return;
    const currentCount = currentGroups.length;
    const toRemove =
      currentCount > 0
        ? currentGroups.filter((current) => !SelectedGroups.includes(current))
        : [];
    const toAdd =
      currentCount > 0
        ? SelectedGroups.filter((selected) => !currentGroups.includes(selected))
        : SelectedGroups;

    toRemove.forEach(({ GroupName }) =>
      removeFromGroup({ UserName, GroupName }),
    );
    toAdd.forEach(({ GroupName }) => addToGroup({ UserName, GroupName }));
  };
  const updatePolicies = async (UserName: string) => {
    if (areArraysEqual(attachedPolicies, selectedPolicies)) return;
    const currentCount = attachedPolicies.length;
    const toRemove =
      currentCount > 0
        ? attachedPolicies.filter(
            (current) => !selectedPolicies.includes(current),
          )
        : [];
    const toAdd =
      currentCount > 0
        ? selectedPolicies.filter(
            (selected) => !attachedPolicies.includes(selected),
          )
        : selectedPolicies;

    toRemove.forEach(({ PolicyArn }) => detachPolicy({ UserName, PolicyArn }));
    toAdd.forEach(({ PolicyArn }) => attachPolicy({ UserName, PolicyArn }));
  };

  const save = async () => {
    const request = formUtils.trimStringValues(userCreateRequest);
    if (isUserCreation) {
      const user = await userApi(iamService.createUser(request));
      await updateGroups(user.UserName);
      await updatePolicies(user.UserName);

      props.fetchUsers();
      props.onClose();
      return user;
    } else {
      let name: string = user.UserName;
      if (user.UserName != request.UserName) {
        name = await userApi(
          iamService
            .updateUser({
              UserName: user.UserName,
              NewUserName: request.UserName,
            })
            .then((u) => u.UserName),
        );
      }
      await updateGroups(name);
      await updatePolicies(name);

      props.fetchUsers();
      props.onClose();
      return user;
    }
  };

  useEffect(() => {
    if (open) {
      if (isUserCreation) {
        setUserCreateRequest({ UserName: "" });
        setUser(null);
      } else {
        userApi(iamService.getUser(userName)).then(async (user) => {
          setUserCreateRequest({
            UserName: user.UserName,
          });
          setUser(user);
          userApi(iamService.getGroupsForUser(user.UserName)).then((page) => {
            setCurrentGroups(page.data);
            setSelectGroups(page.data);
          });
          userApi(
            iamService
              .listAttachedPolicies({ UserName: user.UserName })
              .then((page) => {
                setAttachedPolicies(page.data);
                setSelectedPolicies(page.data);
              }),
          );
        });
      }

      setErrors({});
    } else {
      setUser(null);
      setCurrentGroups([]);
      setSelectGroups([]);
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <>
      <DetailsPanel
        open={open}
        isLoading={isUserLoading}
        onCloseClick={props.onClose}
        title={isUserCreation ? "Add New User" : "User Details"}
        className={styles.panel}
      >
        {open && (
          <div className={styles["user-info"]}>
            {!!user && (
              <>
                <section>
                  <Label label="id" value={user.UserId} />
                  <Label label="resource arn" value={user.Arn} />
                  {/* <Label
                    label="password last used"
                    value={
                      user.PasswordLastUsed
                        ? Formatters.formatDateTime(user.PasswordLastUsed)
                        : "-"
                    }
                  /> */}
                  <Label
                    label="created at"
                    value={Formatters.formatDateTime(user.CreateDate)}
                  />
                </section>
              </>
            )}

            {isUserCreation && (
              <div className={styles.description}>
                A User is an identity within Myota used to represent a person,
                application, or service that needs access to Myota Cortex
                resources. It provides unique credentials, which allow secure
                interaction with the CLI, or APIs. Users have no permissions by
                default, but can be granted specific access through policies or
                added to groups for easier management. They ensure controlled,
                individualized access while following the principle of least
                privilege.
              </div>
            )}

            <IAMEntityNameSection<IIAMUserRequest>
              className={styles.name}
              request={userCreateRequest}
              onChange={handleChange}
              onError={handleError}
              field={"UserName"}
              type={IAMEntity.User}
              errors={errors}
            />
            <GroupSection
              groups={currentGroups}
              onNewGroupClick={handleAddGroupFormPanelOpen}
              isCreate={isUserCreation}
              onChange={handleGroupChange}
            />

            {!isUserCreation && <AccessKeysSection userName={userName} />}

            <PoliciesSection
              policies={attachedPolicies}
              isCreate={isUserCreation}
              onChange={handlePolicyChange}
              onNewPolicy={handleAddPolicyFormPanelOpen}
            />

            <DetailsPanelFooter
              sticky={true}
              saveButtonText={"Save"}
              saveButtonDisabled={!canProceed()}
              onSaveButtonClick={save}
              onCancelButtonClick={props.onClose}
            />
          </div>
        )}
      </DetailsPanel>
      <IAMGroupPanel
        embedded={true}
        mode={groupPanelFormMode}
        onClose={handleAddGroupFormPanelClose}
      />
      <IAMPolicyPanel
        embedded
        mode={policyPanelFormMode}
        onClose={handleAddPolicyFormPanelClose}
      />
    </>
  );
}
